import { css } from 'styled-components';
import { transitions, typography, spacing } from './designSystem';

/**
 * Terminal buttons.
 *
 * A Pip-Boy has no raised, rounded, drop-shadowed buttons — it has selectable
 * entries that light up. So: square corners, a 1px phosphor rule, and hover that
 * *inverts* the entry (phosphor fill, screen-colored text) instead of lifting it.
 * The `color` prop every caller already passes is honored as the phosphor hue.
 */

// Consistent hover: ignite rather than lift.
export const buttonHoverEffect = css`
  transition: ${transitions.all};

  &:hover:not(:disabled) {
    box-shadow: 0 0 12px ${({ color, theme }) => color || theme.primary},
                inset 0 0 18px rgba(0, 0, 0, 0.35);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

export const buttonDisabledStyle = css`
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    filter: grayscale(0.6);
  }
`;

// Primary button — a selected terminal entry, wrapped in brackets.
export const primaryButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[200]};
  padding: ${spacing[300]} ${spacing[600]};
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize[200]};
  font-weight: ${typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  cursor: pointer;
  text-decoration: none;
  border-radius: 0;

  /* Callers pass a color prop for the accent; fall back to the active phosphor so
     an un-themed button still belongs on the screen. */
  background: transparent;
  border: 1px solid ${({ color, theme }) => color || theme.primary};
  color: ${({ color, theme }) => color || theme.primary} !important;
  box-shadow: 0 0 8px ${({ theme }) => theme.glowSoft}, inset 0 0 18px rgba(0, 0, 0, 0.3);
  text-shadow: 0 0 6px currentColor;

  svg {
    color: currentColor !important;
  }

  /* Bracket the label the way a terminal marks a selectable option. */
  &::before {
    content: "[";
    opacity: 0.55;
    margin-right: ${spacing[100]};
  }

  &::after {
    content: "]";
    opacity: 0.55;
    margin-left: ${spacing[100]};
  }

  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: ${({ color, theme }) => color || theme.primary} !important;
  }

  /* Invert on hover — the entry is now "selected". */
  &:hover:not(:disabled) {
    background: ${({ color, theme }) => color || theme.primary};
    color: ${({ theme }) => theme.background} !important;
    text-shadow: none;

    svg {
      color: ${({ theme }) => theme.background} !important;
    }
  }

  ${buttonHoverEffect}
  ${buttonDisabledStyle}
`;

// Secondary button (minimal/ghost) — an unselected terminal entry.
export const secondaryButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[200]};
  padding: ${spacing[200]} ${spacing[400]};
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize[200]};
  font-weight: ${typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  background: none;
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: ${transitions.all};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.primary};
    text-shadow: 0 0 8px ${({ theme }) => theme.glow};
  }

  ${buttonDisabledStyle}
`;

// Icon button — no scale bounce; a terminal glyph just lights up.
export const iconButtonBase = css`
  background: none;
  border: none;
  border-radius: 0;
  cursor: pointer;
  padding: ${spacing[200]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.primary};
  transition: ${transitions.all};

  &:hover:not(:disabled) {
    text-shadow: 0 0 10px ${({ theme }) => theme.glow};
    filter: brightness(1.4);
  }
`;
